import { FC, useEffect, useMemo, useState } from 'react'
import mclasses from '@pages/Admin/CRM/styles/modal.module.css'
import iclasses from '@pages/Admin/CRM/styles/input.module.css'
import classes from './index.module.css'
import Modal from '@common/Modal'
import { classApi } from '@/api'
import { useAddAlert } from '@/hooks/useAddAlert'
import { IcoRemove } from '@/utils/icons'
import Button from '@UI/New/Button'
import { isError } from '@/helpers/checkQuery'
import { InputNew } from '@UI/InputNew'
import cn from 'classnames'
import { ClassFolder } from '../../ClassesPageHeader'
import { Select } from 'antd'
import Preloader from '@UI/Preloader'
import CreateEditFolderModal from '../CreateEditFolderModal/CreateEditFolderModal'
import { useIsUniversity2035DigitalTraceEnabled } from '@/hooks/useIsUniversity2035DigitalTraceEnabled'
import useFranchises from '@/hooks/useFranchises'
import { useSelector } from 'react-redux'
import { selectProfile } from '@/store/profile.reducer'

interface CreateClassesModalProps {
  allFolders: ClassFolder[]
  loadingAllFolders: boolean
  open: boolean
  onClose: () => void
  onSuccess?: (classId: number) => void
  // onDismiss: () => void
}

const CreateClassesModalContent: FC<CreateClassesModalProps> = ({
  allFolders,
  loadingAllFolders,
  open,
  onSuccess,
  onClose,
}) => {
  const addAlert = useAddAlert()
  const isUniversity2035DigitalTraceEnabled =
    useIsUniversity2035DigitalTraceEnabled()
  const profile = useSelector(selectProfile)
  const isFranchiseManager = profile?.role === 'franchise-manager'
  const [submitting, setSubmitting] = useState(false)
  const { enabled: franchisesEnabled, options: franchiseOptions, autoSelect, loading: franchisesLoading } =
    useFranchises()
  const [franchiseId, setFranchiseId] = useState<number | null>(null)

  const [classTitle, setClassTitle] = useState<string | null>(null)
  const [u2035FlowId, setU2035FlowId] = useState('')
  const [u2035ModuleNum, setU2035ModuleNum] = useState('')
  const [selectedFolderId, setSelectedFolderId] = useState<number>(
    allFolders[0]?.id ?? 0,
  )

  const [openCreateFolderModal, setOpenCreateFolderModal] = useState(false)

  const profileFranchiseIds = useMemo(() => {
    const ids = [
      ...(Array.isArray(profile?.franchise_ids) ? profile.franchise_ids : []),
      ...(Array.isArray(profile?.franchises)
        ? profile.franchises.map((franchise: { id?: number | string }) => franchise?.id)
        : []),
    ]

    return Array.from(new Set(ids.map(Number).filter(Boolean)))
  }, [profile?.franchise_ids, profile?.franchises])

  const availableFranchiseOptions = useMemo(() => {
    if (!isFranchiseManager) {
      return franchiseOptions
    }

    if (profileFranchiseIds.length) {
      return franchiseOptions.filter((option) =>
        profileFranchiseIds.includes(Number(option.value)),
      )
    }

    if (autoSelect) {
      return franchiseOptions.filter((option) => Number(option.value) === Number(autoSelect))
    }

    return franchiseOptions
  }, [autoSelect, franchiseOptions, isFranchiseManager, profileFranchiseIds])

  useEffect(() => {
    if (!franchisesEnabled || !isFranchiseManager || franchisesLoading) return

    const availableIds = availableFranchiseOptions.map((option) =>
      Number(option.value),
    )
    if (!availableIds.length) return
    if (franchiseId && availableIds.includes(Number(franchiseId))) return

    setFranchiseId(availableIds[0])
  }, [
    availableFranchiseOptions,
    franchiseId,
    franchisesEnabled,
    franchisesLoading,
    isFranchiseManager,
  ])

  useEffect(() => {
    if (!isFranchiseManager && autoSelect) setFranchiseId(autoSelect)
  }, [autoSelect, isFranchiseManager])

  useEffect(() => {
    if (loadingAllFolders) return

    if (!allFolders.length) {
      addAlert({
        title: 'Ошибка',
        text: 'Папки не найдены, пожалуйста, сначала создайте папку',
        type: 'error',
      })
      onClose()
      return
    }

    if (allFolders.length) {
      setSelectedFolderId(allFolders[0].id)
    }
  }, [allFolders, loadingAllFolders])

  const handleConfirm = async () => {
    setSubmitting(true)
    classApi
      .create({
        title: classTitle,
        folder_id: selectedFolderId,
        one_pay: true,
        amount: 100,
        mass_add_group: 1,
        u2035_flow_id: u2035FlowId,
        u2035_module_num: u2035ModuleNum,
        franchise_id: franchiseId,
      })
      .then((res) => {
        if (isError(res)) throw new Error()

        onSuccess && onSuccess(res.data.data.id)
        addAlert({
          title: 'Успешно',
          text: `Класс успешщно создан`,
          type: 'success',
        })
      })
      .catch((error) => {
        addAlert({
          title: 'Ошибка',
          text: 'Ошибка создания класса',
          type: 'error',
        })
      })
      .finally(() => setSubmitting(false))
  }

  const handlerAddNewFolder = (folder: ClassFolder) => {
    allFolders.push(folder)
    setSelectedFolderId(folder.id)
  }

  return (
    <>
      <div className={mclasses.modalContainer}>
        <div className={mclasses.modalHeader}>
          <div className={mclasses.modalTitleContainer}>
            <span className={mclasses.modalHeaderTitle}>
              Создание класса/обр. траектории
            </span>
          </div>
          <div className={mclasses.closeIcon} onClick={() => onClose()}>
            <IcoRemove />
          </div>
        </div>
        {loadingAllFolders ? (
          <>
            <Preloader fullScreen="center" />
          </>
        ) : (
          <div className={mclasses.formWrapper}>
            <div>
              <p className={mclasses.label}>Название класса/обр. траектории</p>
              {/*@ts-ignore*/}
              <InputNew
                className={iclasses.newInputDesign}
                placeholder={'Название нового класса/обр. траектории'}
                value={classTitle ?? ''}
                onChange={(e: any) => setClassTitle(e.currentTarget.value)}
                validate={classTitle !== null && !classTitle.length}
                errorText={classTitle?.length ? '' : 'Введите название'}
                typeValid="custom"
              />
            </div>
            <div>
              {franchisesEnabled && (
                <>
                  <p className={mclasses.label}>Филиал</p>
                  <Select<number>
                    className={cn(iclasses.newSelect, classes.selectField)}
                    popupClassName={iclasses.newSelectPopup}
                    placeholder="Выберите филиал"
                    value={franchiseId ?? undefined}
                    onChange={setFranchiseId}
                    options={availableFranchiseOptions}
                    disabled={isFranchiseManager && availableFranchiseOptions.length <= 1}
                    loading={franchisesLoading}
                    showSearch
                    optionFilterProp="label"
                  />
                </>
              )}
              <p className={mclasses.label}>Папка</p>
              <Select<number | string>
                className={cn(iclasses.newSelect, classes.selectField)}
                popupClassName={cn(
                  iclasses.newSelectPopup,
                  classes.selectWithCreateButton,
                )}
                placeholder="Выберите папку"
                value={selectedFolderId}
                onChange={(value) => {
                  if (value === 'create_folder') {
                    setOpenCreateFolderModal(true)
                  } else {
                    setSelectedFolderId(Number(value))
                  }
                }}
                options={[
                  {
                    label: 'Создать папку',
                    value: 'create_folder',
                    'data-create-element': true,
                  },
                  ...allFolders.map((folder) => ({
                    label: folder.title,
                    value: folder.id,
                  })),
                ]}
              />
            </div>
            {isUniversity2035DigitalTraceEnabled && (
              <>
                <div>
                  <p className={mclasses.label}>У2035 flow_id</p>
                  {/*@ts-ignore*/}
                  <InputNew
                    className={iclasses.newInputDesign}
                    placeholder="ID потока У2035"
                    value={u2035FlowId}
                    onChange={(e: any) =>
                      setU2035FlowId(e.currentTarget.value)
                    }
                  />
                </div>
                <div>
                  <p className={mclasses.label}>У2035 module_num</p>
                  {/*@ts-ignore*/}
                  <InputNew
                    className={iclasses.newInputDesign}
                    placeholder="Номер модуля У2035"
                    value={u2035ModuleNum}
                    onChange={(e: any) =>
                      setU2035ModuleNum(e.currentTarget.value)
                    }
                  />
                </div>
              </>
            )}
          </div>
        )}
        <div className={mclasses.actions}>
          <Button color="gray" onClick={() => onClose()}>
            Отменить
          </Button>
          <Button
            color="primary"
            onClick={() => handleConfirm()}
            disabled={
              !classTitle?.length ||
              submitting ||
              loadingAllFolders ||
              (franchisesEnabled && !franchiseId)
            }
            isLoading={submitting}
          >
            Подтвердить
          </Button>
        </div>
      </div>

      {/* Modals */}
      <CreateEditFolderModal
        open={openCreateFolderModal}
        onClose={() => setOpenCreateFolderModal(false)}
        onSuccess={(folder) => {
          handlerAddNewFolder(folder)
          setOpenCreateFolderModal(false)
        }}
      />
    </>
  )
}

const CreateClassesModal: FC<CreateClassesModalProps> = (props) => {
  const { open, onClose } = props

  return (
    <Modal
      active={open}
      className={cn(mclasses.modalWrapper, classes.modalWrapper) as any}
      setActive={() => onClose()}
    >
      <CreateClassesModalContent {...props} />
    </Modal>
  )
}

export default CreateClassesModal
